import { createContext, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { getBooking } from 'lib/selfServiceApi';
import ErrorPage from 'pages/ErrorPage';
import LoadingPage from 'pages/LoadingPage';
import { useUpdateEffect } from 'react-use';

import type { SelfServiceAPI } from 'types';

type ContextValue = {
  booking?: SelfServiceAPI.Booking;
  loading: boolean;
  error?: unknown;
  refetch: () => Promise<void>;
};

export const DetailsBookingContext = createContext<ContextValue>({
  booking: undefined,
  loading: true,
  error: undefined,
  refetch: async () => {},
});

type Props = {
  skipUnauthenticatedRedirection?: boolean;
};

const DetailsBookingProvider: FC<Props> = ({ children, skipUnauthenticatedRedirection }) => {
  const {
    params: { bookingId },
    path,
  } = useRouteMatch<{ bookingId: string }>();

  const pollingIntervalIdRef = useRef<number | undefined>(undefined);
  const [booking, setBooking] = useState<SelfServiceAPI.Booking | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(undefined);

  const refetch = useCallback(async () => {
    if (!bookingId) {
      setBooking(undefined);
      setError(undefined);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      const response = await getBooking(bookingId, { skipUnauthenticatedRedirection });
      setBooking(response.booking);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [bookingId, skipUnauthenticatedRedirection]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useUpdateEffect(() => {
    void refetch();
  }, [path]);

  useEffect(() => {
    if (!booking?.flags?.isFinalising) {
      if (pollingIntervalIdRef.current) {
        window.clearInterval(pollingIntervalIdRef.current);
        pollingIntervalIdRef.current = undefined;
      }

      return;
    }

    if (pollingIntervalIdRef.current) {
      return;
    }

    pollingIntervalIdRef.current = window.setInterval(() => {
      refetch();
    }, 1500);

    return () => {
      if (pollingIntervalIdRef.current) {
        window.clearInterval(pollingIntervalIdRef.current);
        pollingIntervalIdRef.current = undefined;
      }
    };
  }, [booking?.flags?.isFinalising, refetch]);

  useEffect(() => {
    return () => {
      if (pollingIntervalIdRef.current) {
        window.clearInterval(pollingIntervalIdRef.current);
        pollingIntervalIdRef.current = undefined;
      }
    };
  }, []);

  const contextValue = useMemo<ContextValue>(
    () => ({
      booking,
      loading,
      error,
      refetch,
    }),
    [booking, loading, error, refetch],
  );

  if (loading && !booking) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage />;
  }

  return <DetailsBookingContext.Provider value={contextValue}>{children}</DetailsBookingContext.Provider>;
};

export default DetailsBookingProvider;
